"use strict";
//
// Copyright (c) Microsoft. All rights reserved.
// Licensed under the MIT license.
//
// MIT License:
// Permission is hereby granted, free of charge, to any person obtaining
// a copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to
// permit persons to whom the Software is furnished to do so, subject to
// the following conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED ""AS IS"", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
// LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
// OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
// WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
//
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var Interfaces_1 = require("./Interfaces");
exports.AzureAD = function (props) {
    var authenticatedFunction = props.authenticatedFunction, unauthenticatedFunction = props.unauthenticatedFunction, provider = props.provider, forceLogin = props.forceLogin, accountInfoCallback = props.accountInfoCallback;
    var _a = react_1.useState(provider.getAccountInfo()), accountInfo = _a[0], _setAccountInfo = _a[1];
    var _b = react_1.useState(provider.authenticationState), authenticationState = _b[0], _setAuthenticationState = _b[1];
    var _c = react_1.useState(provider.getError()), error = _c[0], _setError = _c[1];
    // On component mounted
    react_1.useEffect(function () {
        provider.registerAuthenticationStateHandler(setAuthenticationState);
        provider.registerAcountInfoHandler(onAccountInfoChanged);
        provider.registerErrorHandler(setError);
        if (props.reduxStore) {
            provider.registerReduxStore(props.reduxStore);
        }
        if (authenticationState === Interfaces_1.AuthenticationState.Unauthenticated && forceLogin) {
            login();
        }
        // Cleanup on unmount
        return function () {
            provider.unregisterAuthenticationStateHandler(setAuthenticationState);
            provider.unregisterAccountInfoHandler(onAccountInfoChanged);
            provider.unregisterErrorHandler(setError);
        };
    }, []);
    var login = react_1.useCallback(function () {
        provider.login();
    }, [provider]);
    var logout = react_1.useCallback(function () {
        if (authenticationState !== Interfaces_1.AuthenticationState.Authenticated) {
            return;
        }
        provider.logout();
    }, [authenticationState, provider]);
    var setAuthenticationState = react_1.useCallback(function (newState) {
        if (newState !== authenticationState) {
            _setAuthenticationState(newState);
            if (newState === Interfaces_1.AuthenticationState.Unauthenticated && forceLogin) {
                login();
            }
        }
    }, [authenticationState, forceLogin]);
    var setError = react_1.useCallback(function (newError) {
        if (newError !== error) {
            _setError(newError);
        }
    }, [error]);
    var onAccountInfoChanged = react_1.useCallback(function (newAccountInfo) {
        _setAccountInfo(newAccountInfo);
        if (accountInfoCallback) {
            // tslint:disable-next-line: no-console
            console.warn('Warning! The accountInfoCallback callback has been deprecated and will be removed in a future release.');
            accountInfoCallback(newAccountInfo);
        }
    }, [accountInfoCallback]);
    // The authentication data to be passed to the children() if it's a function
    var childrenFunctionProps = react_1.useMemo(function () { return ({
        accountInfo: accountInfo,
        authenticationState: authenticationState,
        error: error,
        login: login,
        logout: logout,
    }); }, [accountInfo, authenticationState, error, login, logout]);
    function getChildrenOrFunction(children, childrenProps) {
        if (children) {
            // tslint:disable-next-line: triple-equals
            if (typeof children == 'function' || false) {
                return children(childrenProps);
            }
            else {
                return children;
            }
        }
        else {
            return null;
        }
    }
    // Render logic
    switch (authenticationState) {
        case Interfaces_1.AuthenticationState.Authenticated:
            if (authenticatedFunction) {
                var authFunctionResult = authenticatedFunction(logout);
                // tslint:disable-next-line: no-console
                console.warn('Warning! The authenticatedFunction callback has been deprecated and will be removed in a future release.');
                if (authFunctionResult) {
                    return authFunctionResult;
                }
            }
            // If there is no authenticatedFunction, or it returned null, render the children
            return getChildrenOrFunction(props.children, childrenFunctionProps);
        case Interfaces_1.AuthenticationState.Unauthenticated:
            if (unauthenticatedFunction) {
                // tslint:disable-next-line: no-console
                console.warn('Warning! The unauthenticatedFunction callback has been deprecated and will be removed in a future release.');
                return unauthenticatedFunction(login) || null;
            }
            // Only return the children if it's a function to pass the current state to
            //  Otherwise the content should be restricted until authenticated
            var functionOrChildren = getChildrenOrFunction(props.children, childrenFunctionProps);
            return functionOrChildren === props.children ? null : functionOrChildren;
        default:
            return null;
    }
};
exports.AzureAD.displayName = 'AzureAD';
//# sourceMappingURL=AzureAD.js.map